import { Fragment } from "react";
import { Button, Col, Modal, Row, Spin } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import InlineSvg from "react-inlinesvg";
import numeral from "numeral";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { v4 as uuidv4 } from "uuid";

import { icons } from "@constants/icons";
import {
  IFollowerInterface,
  IMasterPlant,
  IMillWatch,
  IProductGroupInterface,
  TDataConfirm,
} from "@components/Form/interface";
import { colors } from "@constants/colors";
import { timeUTCFormat } from "@utils/time";
import countryLangStore from "@zustand/countryLang";
import { IMasterMethod, IMasterOrigin } from "@zustand/interface";
import configStore from "@zustand/configStore";
import { checkCondition, checkEmptyString } from "@utils/checkEmpty";
import { handleValueOr } from "@utils/manageValue";

dayjs.extend(utc);

type Props = {
  visible: boolean;
  onOk: () => void;
  onCancel: () => void;
  loading?: boolean;
  data?: TDataConfirm;
};

const ModalConfirm = ({
  visible,
  onOk,
  onCancel,
  loading = false,
  data,
}: Props) => {
  const { t } = useTranslation();
  const { masterMethod, masterOrigin } = countryLangStore();
  const { config } = configStore();
  const { dataForm, plantsData, productGroupData, VenderDetail } = data ?? {};
  const { vendorname, address1, address2, zipcode } = VenderDetail ?? {};

  const getProductGroup = productGroupData?.find(
    (val: IProductGroupInterface) =>
      val.productGroupCode === dataForm?.product_code,
  )?.productGroupName;

  const getPlantsData = plantsData?.find(
    (val: IMasterPlant) => val.plantCode === dataForm?.factory_code,
  )?.plantName;

  const getPlantsDataMove = plantsData?.find(
    (val: IMasterPlant) => val.plantCode === dataForm?.move_factory_code,
  )?.plantName;

  const getPlantsLotNo = dataForm?.lot_no;

  return (
    <Contents id="modal-confirm">
      <Modal
        title={t("Modal:confirm.title")}
        open={visible}
        onOk={() => (loading ? {} : onOk())}
        onCancel={() => (loading ? {} : onCancel())}
        width={800}
        getContainer={() =>
          document.getElementById("modal-confirm") as HTMLElement
        }
        footer={
          <>
            <Button
              type="text"
              onClick={onCancel}
              disabled={loading}
              className="footer-cancel"
              size="large"
            >
              <InlineSvg src={icons.arrow} /> {t("Home:btn.cancel")}
            </Button>
            <Button
              type="text"
              onClick={onOk}
              className="footer-save"
              disabled={loading}
              size="large"
            >
              {t("Home:btn.save")}
              <InlineSvg src={icons.done} />
            </Button>
          </>
        }
      >
        {!loading && (
          <Row className="row-list">
            <Col xs={{ span: 12 }}>{t("Home:product")}</Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {`${checkEmptyString(getProductGroup)}`}
            </Col>
            <Col xs={{ span: 12 }}>{t("Modal:confirm.label.cf-factory")}</Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {`${checkEmptyString(getPlantsData)}`}
            </Col>
            {getPlantsDataMove && (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Sell-Transport:select_label_destinaion_factory")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(getPlantsDataMove)}`}
                </Col>
              </>
            )}
            {Boolean(config?.configFormTransaction?.lot_no?.display) && (
              <>
                <Col xs={{ span: 12 }}>{t("Sell-Transport:lot_no_label")}</Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(getPlantsLotNo, "-")}`}
                </Col>
              </>
            )}
            <Col xs={{ span: 12 }}>{t("Modal:confirm.label.seller")}</Col>
            <Col
              xs={{ span: 12 }}
              className="row-list__data"
            >{`${checkEmptyString(dataForm?.vendor_code)}`}</Col>
            <Col xs={{ span: 12 }}>{t("Modal:confirm.label.information")}</Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {`${checkEmptyString(vendorname)} ${checkEmptyString(
                address1,
              )} ${checkEmptyString(address2)} ${checkEmptyString(zipcode)}  `}
            </Col>

            {dataForm?.po_number && (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Sell-Transport:transport.label.po_number")}
                </Col>
                <Col
                  xs={{ span: 12 }}
                  className="row-list__data"
                >{`${checkEmptyString(dataForm?.po_number)}`}</Col>
              </>
            )}

            {dataForm?.mill_code_name ? (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Modal:confirm.label.mill-code")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(dataForm?.mill_code_name)}`}
                </Col>
              </>
            ) : (
              <></>
            )}

            {dataForm?.original_weight && (
              <>
                <Col xs={{ span: 12 }}>{t("Modal:confirm.label.product")}</Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${numeral(dataForm?.original_weight).format("0,0")} kg.`}
                </Col>
              </>
            )}

            {dataForm?.mill &&
              dataForm?.mill?.length > 0 &&
              dataForm?.mill?.map((val: IMillWatch) => {
                const millData = JSON.parse(val.mill_data ?? "{}");

                const { province, district, subdistrict, mill_name } = millData;
                const addressMill = `${province ? province + "," : ""} ${
                  district ? district + "," : ""
                } ${subdistrict ?? ""}`;
                if (val.mill_data)
                  return (
                    <>
                      <Col xs={{ span: 12 }} className="row-mill">
                        {t("Search:search.text.establishment")}
                      </Col>
                      <Col xs={{ span: 12 }} className="row-mill">
                        {`${mill_name}`}
                      </Col>
                      <Col xs={{ span: 12 }} className="row-mill">
                        {t("Search:search.text.address")}
                      </Col>
                      <Col xs={{ span: 12 }} className="row-mill">
                        {`${addressMill}`}
                      </Col>
                      <Col xs={{ span: 12 }}>
                        {t("Search:search.text.origin_weight")}
                      </Col>
                      <Col xs={{ span: 12 }}>
                        {`${numeral(val?.mill_weight).format("0,0")} kg.`}
                      </Col>
                    </>
                  );
              })}

            {Boolean(config?.configFormTransaction?.method?.display) && (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Sell-Transport:transport.label.method")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(
                    masterMethod.find(
                      (e: IMasterMethod) =>
                        e.master_method_code === dataForm?.method,
                    )?.name,
                  )} 
            `}
                </Col>
                <Col xs={{ span: 12 }}>
                  {t("Search:search.text.method_quantity")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${handleValueOr(dataForm?.method_quantity, "-")} 
            `}
                </Col>
              </>
            )}
            {Boolean(config?.configFormTransaction?.origin?.display) && (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Sell-Transport:transport.label.origin")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(
                    masterOrigin.find(
                      (e: IMasterOrigin) => e.id === dataForm?.origin,
                    )?.name,
                  )} 
            `}
                </Col>
              </>
            )}

            <Col xs={{ span: 12 }}>
              {t("Search:search.text.transport_vehicle")}
            </Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {` ${checkEmptyString(
                dataForm?.vehicle_registration_province,
              )} ${checkEmptyString(dataForm?.vehicle_registration)} 
            `}
            </Col>
            {dataForm?.trailer_registration && (
              <>
                <Col xs={{ span: 12 }}>
                  {t("Search:search.text.trailer_registration")}
                </Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(
                    dataForm?.trailer_registration_province,
                  )} ${checkEmptyString(dataForm?.trailer_registration)} `}
                </Col>
              </>
            )}

            <Col xs={{ span: 12 }}>{t("Search:search.text.driver_name")}</Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {`
             ${checkEmptyString(dataForm?.driver_name)}
             ${checkEmptyString(dataForm?.driver_surname)} 
            `}
              {dataForm?.driver_temp &&
                `
             ${dataForm?.driver_temp}°C
            `}
            </Col>
            {dataForm?.driver_id && (
              <>
                <Col xs={{ span: 12 }}>{t("Search:search.text.driver_id")}</Col>
                <Col xs={{ span: 12 }} className="row-list__data">
                  {`${checkEmptyString(dataForm?.driver_id)}`}
                </Col>
              </>
            )}
            <Col xs={{ span: 12 }}>{t("Search:search.text.driver_phone")}</Col>
            <Col xs={{ span: 12 }} className="row-list__data">
              {`
             ${checkEmptyString(dataForm?.driver_phone)}
            `}
            </Col>

            {dataForm?.follower && dataForm?.follower?.length > 0 && (
              <>
                <Col xs={{ span: 12 }} className="row-list__length">
                  {t("Sell-Transport:transport.header.follower")}
                </Col>
                <Col
                  xs={{ span: 12 }}
                  className="row-list__data row-list__length"
                >
                  {dataForm?.follower?.map((val: IFollowerInterface) => {
                    return (
                      <div key={uuidv4()}>
                        {`${val.name_follower} ${checkEmptyString(val.surname_follower)} ${checkCondition(val.temp_follower, "(" + val.temp_follower + "°C)", "")}`}
                        <br />
                      </div>
                    );
                  })}
                </Col>
              </>
            )}

            {dataForm?.check_booking && (
              <>
                <Col xs={{ span: 12 }} className="row-list__length">
                  {t("Search:search.text.time_to_factory")}
                </Col>
                <Col
                  xs={{ span: 12 }}
                  className="row-list__data row-list__length"
                >
                  {`${t("Reserve:detail.storage")} ${checkEmptyString(
                    dataForm?.plan_location,
                    "-",
                  )}`}
                  <br />
                  {`${t("Reserve:detail.time")} ${timeUTCFormat(
                    checkEmptyString(dataForm?.plan_time?.toString()),
                  )}`}
                </Col>
              </>
            )}
            {dataForm?.most_shipping &&
              dataForm?.most_shipping?.length > 0 &&
              dataForm?.most_shipping?.map((value: string, index: number) => {
                const labelName =
                  dataForm?.[`master_choices_name_${value}_${index}`];
                const textExtra = dataForm?.[`extra_${value}_${index}`];
                const extraText = checkCondition(
                  textExtra,
                  ` : ${textExtra}`,
                  "",
                );
                const labelText = `(${labelName}${extraText})`;
                return (
                  <Fragment key={uuidv4()}>
                    <Col xs={{ span: 12 }}>
                      {`${t("Search:search.text.most_shipping")} (${
                        index + 1
                      })`}
                    </Col>
                    <Col xs={{ span: 12 }} className="row-list__data">
                      {value} {checkCondition(labelName, labelText, "")}
                    </Col>
                  </Fragment>
                );
              })}
          </Row>
        )}
        {loading && <Spin size="large" className="loading" />}
      </Modal>
    </Contents>
  );
};

export default ModalConfirm;

export const Contents = styled.div`
  .ant-modal {
    top: 15px;
  }
  .ant-modal-content {
    box-shadow: 0 0.5rem 1rem rgb(0 0 0 / 50%);
    border-radius: 0.3rem;
    padding: 0px;
  }
  .ant-modal-close {
    color: white;
  }
  .ant-modal-header {
    background: ${colors.green721};
    text-align: start;
    border-radius: 0.3rem 0.3rem 0 0;
    padding: 20px 24px;
    .ant-modal-title {
      color: #fff;
    }
  }
  .ant-modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 0 0 0.3rem 0.3rem;
    padding: 18px 24px;
    .ant-btn-text {
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #fff;
      :hover {
        opacity: 1;
      }
    }
    .ant-btn-ghost[disabled],
    .ant-btn-ghost[disabled]:active,
    .ant-btn-ghost[disabled]:focus,
    .ant-btn-ghost[disabled]:hover {
      color: rgba(0, 0, 0, 0.25);
      > svg > path {
        stroke: rgba(0, 0, 0, 0.25);
        fill: rgba(0, 0, 0, 0.25);
      }
    }
    .footer-save {
      display: flex;
      align-items: center;
      justify-content: center;
      background: ${colors.green721};
      color: white;
      width: 49%;
    }
    .footer-cancel {
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      border: 1px solid ${colors.graynormal};
      color: ${colors.graynormal};
      width: 49%;
    }
    .footer-save-cpi {
      &.footer-save-cpi:hover {
        opacity: 0.8 !important;
      }
      color: ${colors.white} !important;
      background: ${colors.green721} !important;
      width: 49%;
      display: flex;
      align-items: center;
      justify-content: center;
      .ant-btn-icon {
        display: flex;
        align-items: center;
        svg > path {
          fill: ${colors.white};
        }
      }
    }
  }
  .ant-modal-body {
    padding: 0;
  }
  .row-list {
    text-align: start;
    > div {
      border-bottom: 0.5px solid #e5eaee;
      padding: 14px 14px 14px 30px;
      font-size: 20px;
    }
    &__data {
    }
    &__length {
      border-bottom: 0px !important;
    }
  }
  .row-mill {
    border-bottom: 0px !important;
    padding-bottom: 0px !important;
  }
  .text-bold {
    font-weight: 600;
    font-size: 18px;
    text-align: start;
  }
  .text-medium {
    font-weight: 300;
    font-size: 18px;
    text-align: start;
  }
  .padding-20 {
    padding: 20px 40px;
  }
`;
